import API_PATHS from '@base/features/api/constants/apiPaths';
import {
  Competitor,
  RequestTournamentsList,
  RequestTourney,
  RequestTourneyScheme,
  TournamentStatistics,
} from './interfaces';
import { ResponseCode } from '@base/utils/constants';
import { deserialize } from 'object-deserializer';
import {
  competitorObjectDeserializer,
  requestTournamentListDeserializer,
  requestTourneyDeserializer,
  requestTourneySchemeDeserializer,
  tournamentStatisticsDeserializer,
} from './deserializers';
import { ResponseError } from '@base/features/auth/model/interfaces';
import { responseErrorDeserializer } from '@base/features/user/model/deserializers';
import { TournamentsService } from './interfaces';
import { RangeValue } from '@ui-kit/components/rangePicker';
import addRangeToParams from '@base/utils/addRangeToParams';
import { IApiClient } from '@base/features/api/model/apiClient';

class TournamentsServiceImpl implements TournamentsService {
  constructor(private gameId: number, private apiClient: IApiClient) {}

  async getTournamentsStatistics(range?: RangeValue): Promise<TournamentStatistics> {
    /* const sessionId = this.userService.getUserSession().id; */
    /* const currentUser = this.userService.getUserSession().name; */
    const params = addRangeToParams({ json: 1 }, range);
    const res = await this.apiClient.authGet(params, API_PATHS.tourneyStats);
    const data = await res.json();
    return deserialize(data, tournamentStatisticsDeserializer);
  }

  async getTournamentsListForGameId(): Promise<RequestTournamentsList | ResponseError> {
    const res = await this.apiClient.authPost(
      {
        do: 'tourneys',
        gameid: this.gameId,
        count: 20,
      },
      API_PATHS.api
    );

    const data = await res.json();
    if (data.code === 0) {
      return deserialize(data, requestTournamentListDeserializer);
    } else {
      console.error(data);
      return deserialize(data, responseErrorDeserializer);
    }
  }

  async getTourney(tid: string): Promise<RequestTourney> {
    const res = await this.apiClient.authPost(
      {
        do: 'tourneys',
        gameid: this.gameId,
        tid: tid,
      },
      API_PATHS.api
    );

    const data = await res.json();
    return deserialize(data, requestTourneyDeserializer);
  }

  async getCompetitorsTourney(tid: string): Promise<Competitor[] | void> {
    const res = await this.apiClient.authPost(
      {
        do: 'tourneys',
        gameid: this.gameId,
        tid: tid,
      },
      API_PATHS.api
    );
    if (!res) throw Error('missing response');
    const data = await res.json();
    if ((data.code as ResponseCode) === ResponseCode.SUCCESS) {
      return deserialize(data, (o) =>
        o.required('tourney.competitors').asArrayOfObjects(competitorObjectDeserializer)
      );
    }
  }

  async getTourneyScheme(tid: string): Promise<RequestTourneyScheme> {
    const res = await this.apiClient.authPost(
      {
        do: 'tourney_brackets',
        tid: tid,
      },
      API_PATHS.api
    );
    const data = await res.json();
    return deserialize(data, requestTourneySchemeDeserializer);
  }

  async signUpTournament(tid: string): Promise<number | undefined> {
    const res = await this.apiClient.authPost(
      {
        do: 'tourney_signup',
        tid: tid,
        json: 1,
      },
      API_PATHS.api
    );
    if (!res) throw new Error('missing response');
    const data = await res.json();
    return data.code;
  }

  async signOutTournament(tid: string): Promise<number | undefined> {
    const res = await this.apiClient.authPost(
      {
        do: 'tourney_withdraw',
        tid: tid,
        json: 1,
      },
      API_PATHS.api
    );
    if (!res) throw new Error('missing response');
    const data = await res.json();
    return data.code;
  }

  async getTournamentOptions(tid: string) {
    const res = await this.apiClient.post(
      {
        do: 'timecontrol',
        tid,
      },
      API_PATHS.api
    );
    return await res.json();
  }

  async calculateTournamentPrizes(np: number, fee: number, extra: string, ps: string) {
    const params = { np, fee, extra, ps, json: 1 };
    const response = await this.apiClient.get(params, API_PATHS.calc);
    return await response.json();
  }
}

export default TournamentsServiceImpl;
